import React, {useState} from 'react'
import {useFieldArray, useWatch} from 'react-hook-form'
import {ScrollableSelect} from './ScrollableSelect'
import {Button} from '../../accountManagement/shared/Button'

const FONT = "'Inter', 'Helvetica Neue', Arial, sans-serif"
const ORANGE_PRIMARY = {backgroundColor: '#FF5000', borderColor: '#FF5000'}

const pillStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  background: '#f3f4f6',
  border: '1px solid #e5e7eb',
  borderRadius: '100px',
  padding: '6px 10px 6px 14px',
  fontFamily: FONT,
  fontSize: '14px',
  color: '#374151',
  lineHeight: 1.4,
}

const pillXStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  background: '#d1d5db',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontSize: '13px',
  color: '#6b7280',
  lineHeight: 1,
  flexShrink: 0,
}

const addInputStyle = {
  flex: 1,
  height: '38px',
  border: '1px solid #e5e7eb',
  borderRadius: '6px',
  padding: '0 10px',
  fontFamily: FONT,
  fontSize: '13px',
  color: '#374151',
  outline: 'none',
  boxSizing: 'border-box',
}

function PillTagList({
  control,
  name,
  fieldKey,
  type = 'text',
  min,
  options,
  placeholder,
}) {
  const {fields, append, remove} = useFieldArray({control, name})
  const watched = useWatch({control, name}) || []
  const [pendingValue, setPendingValue] = useState('')
  const [dupError, setDupError] = useState('')

  const usedValues = watched.map(item => item?.[fieldKey]).filter(Boolean)
  const availableOptions = options
    ? options.filter(o => !usedValues.includes(o.value))
    : null

  const canAdd = pendingValue.trim() !== ''

  const handleAdd = () => {
    if (!canAdd) return
    if (usedValues.includes(pendingValue.trim())) {
      setDupError('This value has already been added')
      return
    }
    setDupError('')
    append({[fieldKey]: pendingValue.trim()})
    setPendingValue('')
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  return (
    <div style={{marginTop: '10px'}}>
      {fields.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            marginBottom: '12px',
          }}
        >
          {fields.map((field, index) => {
            const val = watched[index]?.[fieldKey] || ''
            const displayLabel = options
              ? options.find(o => o.value === val)?.label || val
              : val
            if (!val) return null
            return (
              <div key={field.id} style={pillStyle}>
                {displayLabel}
                <button
                  type="button"
                  onClick={() => remove(index)}
                  style={pillXStyle}
                  aria-label={`Remove ${displayLabel}`}
                >
                  &#xd7;
                </button>
              </div>
            )
          })}
        </div>
      )}
      <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
        <div style={{display: 'flex', gap: '8px'}}>
          {options ? (
            <div style={{flex: 1}}>
              <ScrollableSelect
                options={availableOptions || []}
                value={pendingValue}
                onChange={val => {
                  setPendingValue(val)
                  setDupError('')
                }}
                placeholder={placeholder || 'Select…'}
              />
            </div>
          ) : (
            <input
              type={type}
              min={min}
              value={pendingValue}
              onChange={e => {
                setPendingValue(e.target.value)
                setDupError('')
              }}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || 'Enter value…'}
              style={addInputStyle}
            />
          )}
          <Button
            variant="primary"
            buttonType="outlined"
            style={{
              ...ORANGE_PRIMARY,
              height: '38px',
              padding: '0 14px',
              fontSize: '13px',
              opacity: canAdd ? 1 : 0.5,
              whiteSpace: 'nowrap',
            }}
            disabled={!canAdd}
            onClick={handleAdd}
          >
            + Add
          </Button>
        </div>
        {dupError && (
          <p
            style={{
              fontFamily: FONT,
              fontSize: '12px',
              color: '#dc2626',
              margin: 0,
            }}
          >
            {dupError}
          </p>
        )}
      </div>
    </div>
  )
}

function CompatiblePlansList({control, name}) {
  const {fields, append, remove} = useFieldArray({control, name})
  const watched = useWatch({control, name}) || []
  const [pendingCode, setPendingCode] = useState('')
  const [pendingDesc, setPendingDesc] = useState('')
  const [dupError, setDupError] = useState('')

  const usedCodes = watched.map(p => p?.planCode).filter(Boolean)
  const canAdd = pendingCode.trim() !== ''

  const handleAdd = () => {
    if (!canAdd) return
    if (usedCodes.includes(pendingCode.trim())) {
      setDupError('This plan code has already been added')
      return
    }
    setDupError('')
    append({planCode: pendingCode.trim(), planDesc: pendingDesc.trim()})
    setPendingCode('')
    setPendingDesc('')
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  return (
    <div style={{marginTop: '10px'}}>
      {fields.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            marginBottom: '12px',
          }}
        >
          {fields.map((field, index) => {
            const code = watched[index]?.planCode || ''
            const desc = watched[index]?.planDesc || ''
            if (!code) return null
            const label = desc ? `${code} — ${desc}` : code
            return (
              <div key={field.id} style={pillStyle}>
                {label}
                <button
                  type="button"
                  onClick={() => remove(index)}
                  style={pillXStyle}
                  aria-label={`Remove ${code}`}
                >
                  &#xd7;
                </button>
              </div>
            )
          })}
        </div>
      )}
      <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
        <div style={{display: 'flex', gap: '8px'}}>
          <input
            type="text"
            value={pendingCode}
            onChange={e => {
              setPendingCode(e.target.value)
              setDupError('')
            }}
            onKeyDown={handleKeyDown}
            placeholder="Plan Code *"
            style={addInputStyle}
          />
          <input
            type="text"
            value={pendingDesc}
            onChange={e => setPendingDesc(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Plan Description"
            style={addInputStyle}
          />
          <Button
            variant="primary"
            buttonType="outlined"
            style={{
              ...ORANGE_PRIMARY,
              height: '38px',
              padding: '0 14px',
              fontSize: '13px',
              opacity: canAdd ? 1 : 0.5,
              whiteSpace: 'nowrap',
            }}
            disabled={!canAdd}
            onClick={handleAdd}
          >
            + Add
          </Button>
        </div>
        {dupError && (
          <p
            style={{
              fontFamily: FONT,
              fontSize: '12px',
              color: '#dc2626',
              margin: 0,
            }}
          >
            {dupError}
          </p>
        )}
      </div>
    </div>
  )
}

export {PillTagList, CompatiblePlansList}
